'use client';

import { useEffect, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

const SNOOZE_KEY = 'doogler_install_notify_snooze_until';
const INSTALLED_KEY = 'doogler_pwa_installed';
const SNOOZE_MS = 3 * 24 * 60 * 60 * 1000; // 3 days
const SHOW_DELAY_MS = 3000;

// VAPID public key is converted to the Uint8Array applicationServerKey expects.
function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = window.atob(base64);
  const output = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i += 1) output[i] = raw.charCodeAt(i);
  return output;
}

// Creates a Web Push subscription and persists it server-side so the daily
// cron can reach this browser. Safe to call repeatedly (subscribe is idempotent
// and the API upserts by endpoint).
async function subscribeToPush() {
  const vapidKey = process.env.NEXT_PUBLIC_VAPID_PUBLIC_KEY;
  if (!vapidKey || !('serviceWorker' in navigator) || !('PushManager' in window)) {
    return;
  }
  try {
    const registration = await navigator.serviceWorker.ready;
    const existing = await registration.pushManager.getSubscription();
    const subscription =
      existing ||
      (await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(vapidKey),
      }));

    await fetch('/api/push/subscribe', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ subscription }),
    });
  } catch (err) {
    // Subscription is best-effort; failure shouldn't disrupt the UI.
    console.error('Push subscription failed:', err);
  }
}

/**
 * Home-page nudge that appears 3s after landing, offering to enable job
 * notifications and/or install the app to the home screen.
 *
 * Behaviour:
 *  - Skips entirely if the app is already installed AND notification permission
 *    is already decided (granted or denied) — i.e. "already done it".
 *  - Dismissing (✕) snoozes the prompt for 3 days via localStorage, then it
 *    returns. Acting on a CTA removes that CTA; when none remain it closes.
 *  - On iOS (no install prompt API) the install CTA shows Share-sheet steps.
 */
export default function InstallNotifyPrompt() {
  const [visible, setVisible] = useState(false);
  const [installed, setInstalled] = useState(false);
  const [needNotify, setNeedNotify] = useState(false);
  const [isIOS, setIsIOS] = useState(false);
  const [hint, setHint] = useState(false);
  const deferredPrompt = useRef(null);

  useEffect(() => {
    if (typeof window === 'undefined') return;

    // Register the minimal service worker (enables install + future push).
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.register('/sw.js').catch(() => {});
    }

    // Capture Chrome's install prompt so we can trigger it from our button.
    const onBeforeInstall = (e) => {
      e.preventDefault();
      deferredPrompt.current = e;
    };
    const onInstalled = () => {
      deferredPrompt.current = null;
      setInstalled(true);
      // Remember it so we skip the install CTA even when they later open the
      // site in a normal browser tab (where display-mode is "browser").
      try {
        localStorage.setItem(INSTALLED_KEY, '1');
      } catch {}
    };
    window.addEventListener('beforeinstallprompt', onBeforeInstall);
    window.addEventListener('appinstalled', onInstalled);

    const timer = setTimeout(() => {
      // Respect an active snooze.
      const snoozeUntil = Number(localStorage.getItem(SNOOZE_KEY) || 0);
      if (snoozeUntil && Date.now() < snoozeUntil) return;

      const standalone =
        window.matchMedia('(display-mode: standalone)').matches ||
        window.navigator.standalone === true;
      // Currently launched as an app, OR we recorded an install on a past visit
      // (covers the case of an installed user browsing in a normal tab).
      const alreadyInstalled = standalone || localStorage.getItem(INSTALLED_KEY) === '1';
      // If it's running standalone, make sure the persistent flag is set too.
      if (standalone) {
        try {
          localStorage.setItem(INSTALLED_KEY, '1');
        } catch {}
      }

      const notifSupported = 'Notification' in window;
      const notifPending = notifSupported && Notification.permission === 'default';

      const ua = window.navigator.userAgent || '';
      const ios = /iphone|ipad|ipod/i.test(ua);

      setInstalled(alreadyInstalled);
      setNeedNotify(notifPending);
      setIsIOS(ios);

      // Nothing actionable -> "already done it", stay hidden.
      const canOfferInstall = !alreadyInstalled;
      if (!notifPending && !canOfferInstall) return;

      setVisible(true);
    }, SHOW_DELAY_MS);

    return () => {
      clearTimeout(timer);
      window.removeEventListener('beforeinstallprompt', onBeforeInstall);
      window.removeEventListener('appinstalled', onInstalled);
    };
  }, []);

  // Auto-close once there's nothing left to do.
  useEffect(() => {
    if (visible && installed && !needNotify) setVisible(false);
  }, [visible, installed, needNotify]);

  const snoozeAndClose = () => {
    try {
      localStorage.setItem(SNOOZE_KEY, String(Date.now() + SNOOZE_MS));
    } catch {}
    setVisible(false);
  };

  const handleNotify = async () => {
    if (!('Notification' in window)) return;
    try {
      const permission = await Notification.requestPermission();
      // Whether granted or denied, the choice is made — stop offering it.
      setNeedNotify(false);
      if (permission === 'granted') {
        await subscribeToPush();
      }
    } catch {
      setNeedNotify(false);
    }
  };

  const handleInstall = async () => {
    const dp = deferredPrompt.current;
    if (dp) {
      dp.prompt();
      try {
        const { outcome } = await dp.userChoice;
        if (outcome === 'accepted') setInstalled(true);
      } catch {}
      deferredPrompt.current = null;
    } else {
      // No native prompt (iOS, or criteria not yet met) -> show manual steps.
      setHint(true);
    }
  };

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          initial={{ opacity: 0, y: 60 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 60 }}
          transition={{ type: 'spring', bounce: 0.25, duration: 0.5 }}
          role="dialog"
          aria-label="Stay updated with Doogler"
          className="fixed inset-x-3 bottom-3 z-[60] mx-auto max-w-sm rounded-2xl border border-white/20 bg-white/95 p-4 shadow-2xl backdrop-blur dark:border-white/10 dark:bg-gray-950/95 sm:inset-x-auto sm:right-4"
        >
          <button
            onClick={snoozeAndClose}
            aria-label="Dismiss"
            className="absolute right-2 top-2 flex h-7 w-7 items-center justify-center rounded-full text-slate-400 transition hover:bg-slate-100 hover:text-slate-700 dark:hover:bg-white/10 dark:hover:text-slate-200"
          >
            <span className="text-lg leading-none">&times;</span>
          </button>

          <div className="flex items-start gap-3">
            {/* eslint-disable-next-line @next/next/no-img-element */}
            <img
              src="/icon-192.png"
              alt="Doogler"
              width={44}
              height={44}
              className="h-11 w-11 flex-shrink-0 rounded-xl"
            />
            <div className="pr-5">
              <h3 className="text-sm font-bold text-slate-900 dark:text-white">
                Want to get notified for new jobs?
              </h3>
              <p className="mt-0.5 text-xs text-slate-500 dark:text-slate-400">
                Get instant alerts and add Doogler to your home screen for one-tap access.
              </p>
            </div>
          </div>

          <div className="mt-3 flex flex-col gap-2">
            {needNotify && (
              <button
                onClick={handleNotify}
                className="flex items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-customBlue to-customBlue px-4 py-2.5 text-sm font-semibold text-white shadow transition hover:opacity-90"
              >
                <span aria-hidden="true">🔔</span> Allow notifications
              </button>
            )}

            {!installed && (
              <button
                onClick={handleInstall}
                className="flex items-center justify-center gap-2 rounded-xl border border-customBlue/40 bg-customBlue/10 px-4 py-2.5 text-sm font-semibold text-customBlue transition hover:bg-customBlue/20 dark:text-customBlue"
              >
                <span aria-hidden="true">➕</span> Add to home screen
              </button>
            )}
          </div>

          {hint && (
            <p className="mt-2 rounded-lg bg-slate-100 px-3 py-2 text-xs text-slate-600 dark:bg-white/5 dark:text-slate-300">
              {isIOS
                ? 'Tap the Share icon, then choose “Add to Home Screen”.'
                : 'Open your browser menu (⋮) and choose “Install app” / “Add to Home screen”.'}
            </p>
          )}
        </motion.div>
      )}
    </AnimatePresence>
  );
}
